import { Box, Divider } from '@chakra-ui/react'
import {
  Encounter,
  encounterFields,
  EncounterSection,
  EncounterSections,
  FieldMapValue,
  prenatalExamField,
} from '@hb/shared'
import { getRowBackground, getRowDarkBackground } from '../utils'
import { AssessmentPlanView } from './sections/AssessmentPlanView'
import { BirthingCenterAdmissionView } from './sections/BirthingCenterAdmissionView'
import { ClinicalRationaleView } from './sections/ClinicalRationaleView'
import { DepressionScreeningView } from './sections/DepressionScreeningView'
import { DvIpvScreeningView } from './sections/DvIpvScreeningView'
import { ExamAllergiesView } from './sections/ExamAllergiesView'
import { ExamMedicationsView } from './sections/ExamMedicationsView'
import { ExamNotesView } from './sections/ExamNotesView'
import { ExamVitalsView } from './sections/ExamVitalsView'
import { FieldSectionView } from './sections/FieldSectionView'
import { FirstObVisitView } from './sections/FirstObVisitView'
import { GeneralIllnessExamView } from './sections/GeneralIllnessExamView'
import { LabsView } from './sections/LabsView'
import { MdmView } from './sections/MdmView'
import { PatientEducationChecklistView } from './sections/PatientEducationChecklistView'
import { PelvicExamView, PhysicalExamView } from './sections/PhysicalExamView'
import { PreVisitContactView } from './sections/PreVisitContactView'
import { RemoteAssessmentView } from './sections/RemoteAssessmentView'
import { ReviewOfSystemsView } from './sections/ReviewOfSystemsView'
import { RiskAssessmentView } from './sections/RiskAssessmentView'
import { SoapNoteView } from './sections/SoapNoteView'
import { SubstanceUseCounselingView } from './sections/SubstanceUseCounselingView'
import { UltrasoundFindingsView } from './sections/UltrasoundFindingsView'
import { UltrasoundHeaderView } from './sections/UltrasoundHeaderView'
import { VisitLocationView } from './sections/VisitLocationView'

export const SectionWrapper = ({
  children,
  index,
  isLast,
}: {
  children: React.ReactNode
  index: number
  isLast: boolean
}) => (
  <>
    <Box
      bg={getRowBackground(index)}
      _dark={{ bg: getRowDarkBackground(index) }}
      px={4}
      py={3}
      w="100%">
      {children}
    </Box>
    {!isLast ? <Divider /> : null}
  </>
)

export const renderSection = (
  section: EncounterSection,
  encounter: Encounter,
  data: FieldMapValue,
): React.ReactNode => {
  switch (section.type) {
    case 'field':
      return <FieldSectionView section={section} data={data} />
    case 'notes':
      return <ExamNotesView data={data} />
    case 'vitals':
      return <ExamVitalsView section={section} data={data} examType={encounter.type} />
    case 'review-of-systems':
      return <ReviewOfSystemsView data={data} />
    case 'risk-assessment':
      return <RiskAssessmentView data={data} />
    case 'visit-location':
      return <VisitLocationView data={data} />
    // case 'pregnancy-header':
    //   return <PregnancyHeaderView encounter={encounter} />
    case 'allergies':
      return <ExamAllergiesView />
    case 'medications-supplements':
      return <ExamMedicationsView />
    case 'mdm':
      return <MdmView data={data} />
    case 'first-ob-visit':
      return <FirstObVisitView data={data} />
    case 'clinical-rationale':
      return <ClinicalRationaleView data={data} />
    case 'assessment-plan':
      return <AssessmentPlanView data={data} />
    case 'icd10-selection':
      return null
    case 'birthing-center-admission':
      return <BirthingCenterAdmissionView data={data} encounter={encounter} />
    case 'soap-note':
      return <SoapNoteView data={data} />
    case 'general-illness-exam':
      return <GeneralIllnessExamView data={data} />
    case 'remote-assessment':
      return <RemoteAssessmentView data={data} />
    case 'physical-exam':
      return <PhysicalExamView data={data} />
    case 'pelvic-exam':
      return <PelvicExamView data={data} />
    case 'labs-diagnostics':
      return <LabsView data={data} />
    case 'pre-visit-contact':
      return <PreVisitContactView data={data} />
    case 'prenatal-exam':
      return <FieldSectionView section={{ type: 'field', field: prenatalExamField }} data={data} />
    case 'ultrasound-header':
      return <UltrasoundHeaderView data={data} />
    case 'ultrasound-findings':
      return <UltrasoundFindingsView data={data} />
    case 'dv-ipv-screening':
      return <DvIpvScreeningView data={data} />
    case 'depression-screening':
      return <DepressionScreeningView data={data} />
    case 'substance-use-counseling':
      return <SubstanceUseCounselingView data={data} />
    case 'patient-education':
      return <PatientEducationChecklistView data={data} />
  }
}

export const generateAllSections = (
  encounter: Encounter,
  encountersArr: Encounter[] | null,
): EncounterSections => {
  const generators = encounterFields[encounter.type](encounter.config as any)
  const formData = (encounter.data ?? {}) as FieldMapValue
  return generators.reduce<EncounterSections>(
    (prev, gen) => [
      ...prev,
      ...gen(encounter.assessmentId ?? null, encountersArr ?? [], formData, prev),
    ],
    [],
  )
}
